import React from 'react';
import { Box, Flex, ListItem, Text, UnorderedList, useColorMode } from '@chakra-ui/react';
import { Container } from 'components/Container';
import { bgColor } from 'config/data';

const SectionTitle = ({ title }) => (
  <Text mb="5px" fontSize="20px" fontWeight="bold">
    {title}
  </Text>
);

const PrivacyPolicy = () => {
  const { colorMode } = useColorMode();
  return (
    <Box
      bg={bgColor[colorMode]}
      as="section"
      id="settings"
      pt={{ base: '5px', md: '20px' }}
      pb={{ base: '25px', md: '20px' }}
      px={['15px', '10px', 0]}
    >
      <Container>
        <Flex minH="60vh" flexDirection={{ base: 'column', md: 'row' }}>
          <Flex
            borderWidth={colorMode === 'dark' ? 0 : 1}
            borderRadius={8}
            flex="1"
            py="50px"
            px="60px"
            flexDirection="column"
            fontSize="14px"
            id="content"
            textAlign="justify"
          >
            <Text fontSize="35px" fontWeight="bold" mb="10px">
              HeyLayer Privacy Policy
            </Text>
            <Text color="gray.500" mb="20px">
              Last Updated: April 28, 2022
            </Text>
            <Text mb="20px">
              Hey you! Thanks for visiting https://heylayer.com (the <b>“Website”</b>). We built the Website with your
              privacy in mind. This Privacy Policy (the <b>“Policy”</b>) describes how we at Layer Technologies, Inc.
              (“HeyLayer”) collect, use, share, and secure your personal information when you visit the Website and the
              products, features, materials, and services we offer (collectively with the Website, the “Services”). The
              Website, together with the Services, shall be referred to as the “Platform.” This Policy also describes
              our policies and procedures for collecting, using, maintaining, protecting and disclosing that
              information.
            </Text>
            <SectionTitle title="Information We Collect from You" />
            <Text mb="20px">
              Personal data (<b>“Personal Data”</b>) means data that allows someone to identify or contact you,
              including, for example, your name, address, telephone number, e-mail address, as well as any other
              non-public information about you that is associated with or linked to any of the foregoing data. Anonymous
              data (<b>“Anonymous Data”</b>) means data, including aggregated and de-identified data, that is not
              associated with or linked to your Personal Data; Anonymous Data does not, by itself, permit the
              identification of individual persons. We collect Personal Data and Anonymous Data, as described below.
            </Text>
            <SectionTitle title="Collection of Personal Data" />
            <Text mb="10px">We collect Personal Data that you knowingly choose to disclose. This may include:</Text>
            <UnorderedList mb="20px">
              <ListItem>
                Your first and last name, e-mail and mailing addresses, Stacks address, HeyLayer password when you
                create an account to log in to our network (“<b>Account</b>”), phone number and any other information
                you directly provide us on or through the Services. This includes information you provide when you
                register or create an account, or request for customer service.
              </ListItem>
              <ListItem>
                Records and copies of your email messages together with your email address and our responses, if you
                choose to correspond with us through email.
              </ListItem>
              <ListItem>
                Information or content you submit to be published or displayed on public areas of the Services or
                transmitted to other users of the Services or third parties (collectively, &quot;<b>User Content</b>
                &quot;). Your User Content is posted on and transmitted to others at your own risk. The Company cannot
                control the actions of other users of the Services with whom you may choose to share your User Content.
                Therefore, we cannot and do not guarantee that your User Content will not be viewed by unauthorized
                persons.
              </ListItem>
              <ListItem>
                Information about any purchase or transactions made on the Services. This includes payment information,
                such as your credit or debit card number and other card information; other account and authentication
                information; and billing, shipping and contact details.
              </ListItem>
            </UnorderedList>
            <Text mb="10px">We also automatically collect certain data. This may include:</Text>
            <UnorderedList mb="20px">
              <ListItem>
                Details of your visits to our Services, including the types of content you view or engage with; the
                features you use; the actions you take; the people or accounts you interact with; the time, frequency
                and duration of your activities; and other information about your use of and actions on the Services.
              </ListItem>
              <ListItem>
                Information about your computer and internet connection, including your computer operating system, IP
                address, browser type and browser language.
              </ListItem>
              <ListItem>
                Information about the location of your device, including GPS location, for purposes of enhancing or
                facilitating the Services. For example, such information may be used to enable the functionality or
                features of the Services that provide you with information about stores near you, or enable you to
                remotely order and pay for the Services. We may also use information about the location of the device
                you are using to help us understand how the Services and functionality are being used and to deliver
                more relevant advertising.
              </ListItem>
            </UnorderedList>
            <SectionTitle title="What We Do With Personal Information We Collect" />
            <Text mb="10px">We use the Personal Information we collect to:</Text>
            <UnorderedList mb="20px">
              <ListItem>
                Make the Platform work and enable you to list, buy, transfer or trade HeyLayer Platform items;
              </ListItem>
              <ListItem>Provide you with critical updates, confirmations or security alerts;</ListItem>
              <ListItem>Provide support or respond to your comments or questions;</ListItem>
              <ListItem>Personalize and improve your experience;</ListItem>
              <ListItem>
                Inform you about products, services, news or promotional opportunities we think might interest you;
              </ListItem>
              <ListItem>Analyze and improve our Platform;</ListItem>
              <ListItem>​​Facilitate the creation of and secure your Account on our network;</ListItem>
              <ListItem>Identify you as a user in our system;</ListItem>
              <ListItem>
                Provide the Services you request, including but not limited to facilitating your cryptocurrency
                transactions through MetaMask (
                <a target="_blank" rel="noreferrer" href="https://metamask.io/" style={{ color: 'blue' }}>
                  https://metamask.io
                </a>
                ) or other wallets;
              </ListItem>
              <ListItem>
                Send you a welcome email to verify ownership of the email address provided when your Account was
                created;
              </ListItem>
              <ListItem>
                Protect you and other users from any conduct that violates the Terms of Use or to prevent abuse or
                harassment of any user;
              </ListItem>
              <ListItem>Display your username next to the digital assets you wish to sell on the Website;</ListItem>
              <ListItem>
                Send you administrative email notifications, such as security or support and maintenance advisories;
              </ListItem>
              <ListItem>
                Send you email notifications related to actions on the Platform, including notifications of offers on
                your digital assets; and
              </ListItem>
              <ListItem>Respond to your inquiries related to employment opportunities or other requests.</ListItem>
            </UnorderedList>
            <SectionTitle title="Creation and Collection of Anonymous Data" />
            <Text mb="20px">
              We may create Anonymous Data records from Personal Data by excluding information (such as your name) that
              makes the data personally identifiable to you. We use this Anonymous Data to analyze request and usage
              patterns so that we may enhance the content of our Services and improve Service navigation. We reserve the
              right to use Anonymous Data for any purpose and to disclose Anonymous Data to third parties without
              restriction.
            </Text>
            <SectionTitle title="Data Retention" />
            <Text mb="20px">
              We may retain your Personal Data as long as you continue to use the Platform, have an account with us, or
              for as long as is necessary to fulfill the purposes outlined in this Privacy Policy. You can ask to close
              your account by contacting us as described below, and we will delete your Personal Data on request. We
              may, however, retain Personal Data for an additional period as is permitted or required under applicable
              laws, for legal, tax or regulatory reasons, or for legitimate and lawful business purposes.
            </Text>
            <SectionTitle title="Data Access and Control " />
            <Text mb="20px">
              You can view, access, edit or delete your personal data for many aspects of the Service via your account
              settings page. You can also make choices about HeyLayer’s use of your data. You can always choose whether
              you want to receive marketing communications from us. You can also opt out from receiving marketing
              communications from us by using the opt-out link on the communication, or by visiting your account
              settings page. We may retain an archived copy of your records as required by law or for legitimate
              business purposes.
            </Text>
            <SectionTitle title="How We Respond to Do Not Track Signals" />
            <Text mb="20px">
              &quot;Do Not Track&quot; is a privacy preference you can set in most web browsers. When you turn on this
              preference, it sends a signal or message to the websites you visit indicating that you do not wish to be
              tracked. Please note that we currently do not respond to Do Not Track browser settings.
            </Text>
            <SectionTitle title="Accessing and Correcting Your Information" />
            <Text mb="20px">
              You may send us an email to request access to, correct or remove any personal information that you have
              provided to us. You may also access, correct or remove your personal information by logging into the
              Website and visiting your account profile page. We cannot delete your personal information except by also
              deleting your account.
            </Text>
            <Text mb="20px">
              We may not accommodate a request to change information if we believe the change would violate any law or
              legal requirement or cause the information to be incorrect. If you delete your User Content from the
              Services or your account, copies of your User Content may remain viewable in cached and archived pages or
              might have been copied or stored by other users of the Services.
            </Text>
            <Text mb="20px" fontSize="24px" fontWeight="bold">
              When We May Share Personal Information
            </Text>
            <Text mb="20px">We disclose your Personal Data as described below:</Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                Third Party Service Providers.
              </Text>{' '}
              We may share your Personal Data with third party service providers to: provide you with the Services that
              we offer you through our Service; to conduct quality assurance testing; to facilitate creation of
              accounts; to provide technical support; and/or to provide other services to HeyLayer.
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                Third Party Creators.
              </Text>{' '}
              We may share your Personal Data with third parties solely with regard to the mailing of content in
              relation to User Content on the Platform. You may request the deletion of your Personal Data or the
              closing of your Account if you do not consent to the sharing of your Personal Data in this manner.
            </Text>
            <Text mb="20px">
              If you&apos;ve recently purchased an NFT via the Platform that came with perks/unlockables, it&apos;s
              possible we&apos;ll need to collect and provide your contact information to the creator in order to get
              that to you. If you have any questions, please contact{' '}
              <a href="mailto:support@heylayer.com" style={{ color: 'blue' }}>
                support@heylayer.com
              </a>{' '}
              We will forward along your info and ask the creator to please respond promptly.
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                Affiliates.
              </Text>{' '}
              We may share some or all of your Personal Data with our parent company, subsidiaries, joint ventures or
              other companies under common control (“Affiliates”), in which case we will require our Affiliates to honor
              this Privacy Policy.
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                Corporate Restructuring.
              </Text>{' '}
              We may share some or all of your Personal Data in connection with or during negotiation of any merger,
              financing, acquisition or dissolution transaction or proceeding involving sale, transfer, divestiture, or
              disclosure of all or a portion of our business or assets. In the event of an insolvency, bankruptcy, or
              receivership, Personal Data may also be transferred as a business asset. If another company acquires our
              company, business or assets, that company will possess the Personal Data collected by us and will assume
              the rights and obligations regarding your Personal Data as described in this Privacy Policy.
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                As Legally Required.
              </Text>{' '}
              Regardless of any choices you make regarding your Personal Data (as described below), HeyLayer may
              disclose Personal Data if it believes in good faith that such disclosure is necessary (a) in connection
              with any legal investigation; (b) to comply with relevant laws or to respond to subpoenas or warrants
              served on HeyLayer; (c) to protect or defend the rights or property of HeyLayer or users of the Service;
              and/or (d) to investigate or assist in preventing any violation or potential violation of the law, this
              Privacy Policy, or our Terms of Use.
            </Text>
            <Text>
              <Text as="span" textDecoration="underline">
                To Enforce our Rights.
              </Text>{' '}
              To enforce or apply this Policy, our Terms of Service (located at
              <a
                href="https://docs.google.com/document/d/e/2PACX-1vQpEv-EHVkYUPeqWoAfTBNfSOa7ARJeE24xXrws1Fz16YrOIUXCqDnK8t0gMFAztbGz0r5mQhc0fAC3/pub"
                target="_blank"
                rel="noreferrer"
                style={{ color: 'blue' }}
              >
                https://docs.google.com/document/d/e/2PACX-1vQpEv-EHVkYUPeqWoAfTBNfSOa7ARJeE24xXrws1Fz16YrOIUXCqDnK8t0gMFAztbGz0r5mQhc0fAC3/pub
              </a>
              ), and other agreements, including for billing and collection purposes.
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                Other Disclosures.
              </Text>{' '}
              We may also disclose your Personal Data to fulfill the purpose for which you provide it; for any other
              purpose disclosed by us when you provide it; or with your consent. We do not sell your Personal Data.
            </Text>
            <Text mb="20px" fontSize="24px" fontWeight="bold">
              Cookies
            </Text>
            <Text mb="20px">
              We also collect basic analytics data through Cookies when you access or use the HeyLayer Platform. A
              <b>“Cookie”</b> is a small piece of data or text file stored on the local hard disk of your computer or
              mobile device. These include our own first-party Cookies and third-party Cookies. Some Cookies are only
              stored temporarily and destroyed each time you close your web browser. Others may remain on your browser
              and may collect and store data for a period of time after you have left the Platform.
            </Text>
            <SectionTitle title="How we use Cookies" />
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                Functionality.
              </Text>{' '}
              Our Platform uses Cookies to ensure it functions properly, including page loads or our chat feature.
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                Performance and Analytics.
              </Text>{' '}
              Our Platform uses Cookies to gather usage and performance data. This enables us to monitor and improve the
              Platform’s performance, our services, and your experience.
            </Text>
            <SectionTitle title="How you can control Cookies" />
            <Text mb="20px">
              If you wish to prevent Cookies from tracking your activity on our Platform or visits across multiple
              websites, you can set your browser to block certain Cookies or notify you when a Cookie is set.
            </Text>
            <Text mb="20px">
              You may opt-out of interest-based advertising in general by visiting the Digital Advertising Alliance’s or
              Network Advertising Initiative’s websites. We are not responsible for the completeness, effectiveness, or
              accuracy of any third-party opt-out options or programs. If you block Cookies, some features that make the
              Platform function properly may not work and your experience may be affected.
            </Text>
            <Text mb="20px" fontSize="24px" fontWeight="bold">
              Your Rights
            </Text>
            <Text mb="20px">
              You may contact us at{' '}
              <a href="mailto:support@heylayer.com" style={{ color: 'blue' }}>
                support@heylayer.com
              </a>{' '}
              to erase, update or correct any Personal Data we collected about you.
            </Text>
            <Text mb="20px">
              You can unsubscribe from commercial or marketing communications from us by contacting us at
              <a href="mailto:support@heylayer.com" style={{ color: 'blue' }}>
                support@heylayer.com
              </a>{' '}
              or following the instructions at the bottom of any email you receive from us.
            </Text>
            <Text mb="20px">
              You can view, access, edit or delete your Personal Data for certain aspects of the Service via your
              Account settings page. You may also have certain additional rights and we will make every effort to
              promptly respond to your requests:
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                The GDPR.
              </Text>{' '}
              If you are a user in the European Economic Area or United Kingdom, you have certain rights under the
              respective European and UK General Data Protection Regulations (the <b>“GDPR”</b>). These include the
              right to (i) request access and obtain a copy of your Personal Data; (ii) request rectification or
              erasure; (iii) object to or restrict the processing of your personal data; and (iv) request portability of
              your personal data. Additionally, if we have collected and processed your personal data with your consent,
              you have the right to withdraw your consent at any time.
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                California.
              </Text>{' '}
              If you are a California resident, you have certain rights under the California Consumer Privacy Act (
              <b>“CCPA”</b>). These include the right to (i) request access to, details regarding, and a copy of the
              personal information we have collected about you and/or shared with third parties; (ii) request deletion
              of the personal information that we have collected about you; and (iii) the right to opt-out of sale of
              your personal information. As the terms are defined under the CCPA, we do not “sell” your “personal
              information.”
            </Text>
            <Text mb="20px">
              Under California Civil Code sections 1798.83-1798.84, California residents are entitled to ask us for a
              notice identifying the categories of personal customer information which we share with our affiliates and
              third parties for marketing purposes and providing contact information for such affiliates and third
              parties. If you are a California resident and would like a copy of this notice, please submit a written
              request to{' '}
              <a href="mailto:support@heylayer.com" style={{ color: 'blue' }}>
                support@heylayer.com
              </a>
              .
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                Exercising Your Rights.
              </Text>{' '}
              If you wish to exercise your rights under the GDPR, CCPA or other applicable data protection or privacy
              laws, please contact us by emailing{' '}
              <a href="mailto:support@heylayer.com" style={{ color: 'blue' }}>
                support@heylayer.com
              </a>
              , specify your request, and reference the applicable law. We may ask you to verify your identity or ask
              for more information about your request. We will consider and act upon any above request in accordance
              with applicable law. We will not discriminate against you for exercising any of these rights.
            </Text>
            <Text mb="20px">
              <Text as="span" textDecoration="underline">
                Disclaimer.
              </Text>{' '}
              Notwithstanding the above, we cannot edit or delete any information that is stored on a blockchain, for
              example the Ethereum blockchain, as we do not have custody or control over any blockchains. The
              information stored on the blockchain may include purchases, sales, and transfers related to your
              blockchain address and NFTs held at that address.
            </Text>
            <SectionTitle title="Minors and Children" />
            <Text mb="20px">
              The Platform and its content are not intended for, nor directed at, children under the age of 13. No one
              under the age of 13 may provide any personal information to or on the Platform. Please note that we do not
              knowingly collect any personally identifiable information from children under the age of 13. If you are
              under the age of 13, please do not attempt to use or register for the Platform or send any information
              about yourself to us, including your name, address, telephone number, or email address. If it is
              determined that we have inadvertently collected or received personally identifiable information from a
              child under the age of 13, we shall immediately take the necessary steps to ensure that such information
              is deleted from our system’s database. If you are a parent or legal guardian and think your child under
              the age of 13 has given us information, you can contact us at{' '}
              <a href="mailto:support@heylayer.com" style={{ color: 'blue' }}>
                support@heylayer.com
              </a>
              .
            </Text>
            <SectionTitle title="Third-Party Links and Services" />
            <Text mb="20px">
              This Policy does not cover the use of tracking technologies by third parties. The Platform may contain
              links, content, advertising, or references to other websites by third parties, including advertisers, ad
              networks and servers, content providers, and application providers. These third parties may use cookies or
              other tracking technologies to collect information about you when you use the Platform. The information
              they collect may be associated with your personal information or they may collect information about your
              online activities over time and across different websites. Please be aware that we do not control these
              third parties&apos; tracking technologies or when and how they may be used. Therefore, HeyLayer does not
              claim nor accept responsibility for any privacy policies, practices, or procedures of any third party. We
              encourage you to read the privacy statements and terms and conditions of linked or referenced websites you
              enter. If you have any questions about an ad or other targeted content, you should contact the responsible
              provider directly.
            </Text>
            <SectionTitle title="The Digital Millennium Copyright Act" />
            <Text mb="20px">
              The Digital Millennium Copyright Act (DMCA) sets forth a Notification and Counter Notification process
              initiated by a party complaining of alleged copyright infringement on the HeyLayer Platform. We may share
              information of the notifying, counter-notifying party, and any involved third parties, such as a
              collector, of an allegedly infringing artwork to facilitate communication between the relevant parties for
              the purposes of complying with or resolving a DMCA dispute or allegation of copyright infringement.
            </Text>
            <SectionTitle title="Important Limitations" />
            <Text mb="20px">Nothing in this Policy restricts our ability to:</Text>
            <UnorderedList mb="20px">
              <ListItem>Comply with applicable law, rules, or regulations, including the DMCA;</ListItem>
              <ListItem>
                Comply with a civil, criminal, or regulatory inquiry, investigation, subpoena, or lawful court order;
              </ListItem>
              <ListItem>
                Cooperate with law enforcement agencies about conduct we reasonably and in good faith believe may
                violate the law;
              </ListItem>
              <ListItem>Protect our rights or our property;</ListItem>
              <ListItem>Enforce our Terms of Service; or,</ListItem>
              <ListItem>Prevent behavior that is (or that we think may be) illegal or unethical.</ListItem>
            </UnorderedList>
            <SectionTitle title="How We Protect Your Information" />
            <Text mb="20px">
              We take reasonable precautions to secure your personal information. We have implemented technical,
              physical, and administrative security measures designed to reduce the risk of loss, misuse, unauthorized
              access, disclosure, or modification of your information. We have put in place appropriate physical,
              electronic, and managerial procedures to safeguard the information we collect. All information you provide
              to us is stored on our secure servers behind firewalls. When you transmit highly sensitive information
              (such as a credit card number) through the Services, we encrypt the transmission of that information using
              Secure Sockets Layer (SSL) technology.
            </Text>
            <Text mb="20px">
              The safety and security of your information also depend on you. Where we have given you (or where you have
              chosen) a password for access to certain parts of the Services, you are responsible for keeping this
              password confidential. We ask you not to share your password with anyone.
            </Text>
            <Text mb="20px">
              While we have employed security technologies and procedures to assist safeguarding your personal
              information, no system or network can be guaranteed to be 100% secure. Unauthorized entry or use, hardware
              or software failure, and other factors may compromise the security of user information at any time. Any
              transmission of personal information is at your own risk. We are not responsible for circumvention of any
              privacy settings or security measures contained on the Website or any other Services.
            </Text>
            <SectionTitle title="Changes to this Policy" />
            <Text mb="20px">
              Our commitment to preserving your privacy will not change, but our Platform may evolve. The effective date
              of each version is identified above. If we make material changes to this Privacy Policy, we will use
              reasonable means to inform you and, where necessary, obtain your consent.
            </Text>
            <SectionTitle title="Questions" />
            <Text mb="20px">
              If you have questions about this Policy or how we treat your Personal Information, please contact us at{' '}
              <a href="mailto:support@heylayer.com" style={{ color: 'blue' }}>
                support@heylayer.com
              </a>
              .
            </Text>
            <SectionTitle title="Terms of Use" />
            <Text mb="20px">
              If you choose to visit the Platform, your visit and any dispute over privacy is subject to this Policy and
              our Terms of Service (located at{' '}
              <a
                href="https://docs.google.com/document/d/e/2PACX-1vQpEv-EHVkYUPeqWoAfTBNfSOa7ARJeE24xXrws1Fz16YrOIUXCqDnK8t0gMFAztbGz0r5mQhc0fAC3/pub"
                target="_blank"
                rel="noreferrer"
                style={{ color: 'blue' }}
              >
                https://docs.google.com/document/d/e/2PACX-1vQpEv-EHVkYUPeqWoAfTBNfSOa7ARJeE24xXrws1Fz16YrOIUXCqDnK8t0gMFAztbGz0r5mQhc0fAC3/pub
              </a>
              ), including limitations on damages, resolution of disputes, and application of the law of the state of
              New York.
            </Text>
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};
export default PrivacyPolicy;
